import { useState } from "react";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { useDebounce } from "use-debounce";
import { useShallow } from "zustand/shallow";
import { SCOrderListResponseType } from "@sellar-io/api-v2/types";
import { capitalize, formatCurrency } from "@sellar-io/common";
import { Tag } from "../../../../../components/tag/Tag";
import { PageCollectionLayout } from "../../../../../components/pageCollection/PageCollectionLayout";
import { TableHeader } from "../../../../../components/table/TableHeader";
import { SELLAR_CREDIT_TAB } from "../../../../../constants/routes";
import { withSearchParams } from "../../../../../utils/withSearchParams";
import {
  useSCOrderListCount,
  useSCOrderListPaginated,
} from "../../../../../hooks/api/v2/sellarCreditManagement/useSCOrderList";
import { SellarCreditOrderFilter } from "./components/SellarCreditOrderFilter";
import { SellarCreditOrderDrawer } from "./components/SellarCreditOrderDrawer";
import { SellarCreditOrderTable } from "./components/SellarCreditOrderTable";
import { SellarCreditOrderCardList } from "./components/SellarCreditOrderCardList";
import {
  SellarCreditOrderSortType,
  SellarCreditOrderSortValues,
} from "../../../stores/SellarCreditTabTypes";
import { useSellarCreditOrderStore } from "../../../stores/SellarCreditOrderStore";
import { Settings } from "../../../constants/tabs";
import { tabsLeft } from "../../../constants/tabs.shared";
import { SellarCreditTabSummary } from "./components/SellarCreditTabSummary";
import { TwoOpenBankingRefreshBanner } from "./components/TwoOpenBankingRefreshBanner";
import { SwitchCreditProviderButton } from "../../../components/SwitchCreditProviderButton";
import { useLegalEntityId } from "../../../../../hooks/api/v2/legalEntity/useLegalEntityId";
import { useLegalEntity } from "../../../../../hooks/api/v2/legalEntity/useLegalEntity";
import { useFeatureFlagStore } from "~/hooks/useFeatureFlags";

export const TwoSellarCreditTab = () => {
  const [active, setActive] = useState(false);
  const [desktopFilter, setDesktopFilter] = useState(false);
  const [mobileFilter, setMobileFilter] = useState(false);
  const [selectedOrder, setSelectedOrder] =
    useState<SCOrderListResponseType[number]>();
  const [clickOutside, setClickOutside] = useState(true);
  const scPaymentAllocationHistoryEnabled = useFeatureFlagStore().isActive(
    "SELLAR_CREDIT_PAYMENT_ALLOCATION_HISTORY",
  );

  const isDesktop = useMediaQuery("(min-width: 1200px)");

  const state = useSellarCreditOrderStore(useShallow((state) => state));

  const storeActions = state;

  const handleTableSort = (columnSort: SellarCreditOrderSortType | null) => {
    storeActions.setColumnSort(columnSort ?? "orderId_desc");
  };

  const [debouncedSearchValue] = useDebounce(state.search, 500);

  const orderTotalMinNum = parseInt(state.orderTotalMin);
  const orderTotalMaxNum = parseInt(state.orderTotalMax);

  const queryParams = {
    search: debouncedSearchValue,
    limit: state.pagination.pageSize,
    offset: state.pagination.pageIndex * state.pagination.pageSize,
    sort: SellarCreditOrderSortValues[state.columnSort]
      ? [SellarCreditOrderSortValues[state.columnSort].mutation]
      : [],
    query: [
      ...(state.business
        ? [
            {
              type: "businessId",
              modifier: "EQUALS",
              value: state.business.id,
            } as const,
          ]
        : []),
      ...(state.user
        ? [
            {
              type: "userId",
              modifier: "EQUALS",
              value: state.user.id,
            } as const,
          ]
        : []),
      ...(state.orderStatuses.length > 0
        ? [
            {
              type: "orderStatus",
              modifier: "IN",
              value: state.orderStatuses,
            } as const,
          ]
        : []),
      ...(!!state.orderTotalMin || !!state.orderTotalMax
        ? [
            {
              type: "orderTotalValue",
              modifier: "BETWEEN",
              value: {
                low: isNaN(orderTotalMinNum) ? 0 : orderTotalMinNum * 100,
                high: isNaN(orderTotalMaxNum)
                  ? 999999999
                  : orderTotalMaxNum * 100,
              },
            } as const,
          ]
        : []),
      ...(!!state.orderDateMin || !!state.orderDateMax
        ? [
            {
              type: "orderDate",
              modifier: "BETWEEN",
              value: {
                low: state.orderDateMin
                  ? dayjs(state.orderDateMin).startOf("day").toDate()
                  : undefined,
                high: state.orderDateMax
                  ? dayjs(state.orderDateMax).endOf("day").toDate()
                  : undefined,
              },
            } as const,
          ]
        : []),
      {
        type: "creditProvider",
        value: "two",
        modifier: "EQUALS",
      } as const,
    ],
  };

  const orderQuery = useSCOrderListPaginated(queryParams, {
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    enabled: isDesktop,
  });

  const orderCountQuery = useSCOrderListCount(queryParams, {
    refetchOnWindowFocus: false,
    enabled: isDesktop,
  });

  const navigate = useNavigate();

  const totalResults = orderCountQuery.data ?? 0;

  const legalEntityId = useLegalEntityId();
  const { data: legalEntity } = useLegalEntity(legalEntityId, {
    enabled: !!legalEntityId,
  });

  const companyHasHokodoAccount = !!legalEntity?.hokodoCompanyId;

  return (
    <PageCollectionLayout
      title="Sellar Credit"
      topComponent={
        companyHasHokodoAccount && (
          <SwitchCreditProviderButton
            onSwitchClick={() =>
              navigate(
                withSearchParams(SELLAR_CREDIT_TAB, { provider: "hokodo" }),
              )
            }
            currentProvider="two"
          />
        )
      }
      tabsLeft={tabsLeft({
        provider: "two",
        scPaymentAllocationHistoryEnabled,
      })}
      rightComponent={<Settings provider="two" />}
    >
      <div
        className={twMerge(
          "container mx-auto max-w-[1300px] bg-zinc-100",
          isDesktop ? "mt-6" : "mt-0",
        )}
      >
        {legalEntity?.twoMerchantCustomer && legalEntityId && (
          <div className="my-3">
            <TwoOpenBankingRefreshBanner
              accountDetailsLastSubmittedAt={
                new Date(
                  legalEntity.twoMerchantCustomer.accountDetailsLastSubmittedAt,
                )
              }
              legalEntityId={legalEntityId}
            />
          </div>
        )}

        <SellarCreditTabSummary />

        <TableHeader
          className={isDesktop ? "mt-4" : ""}
          clickOutside={clickOutside}
          desktopFilterActive={desktopFilter}
          setDesktopFilterActive={setDesktopFilter}
          filter={
            <SellarCreditOrderFilter
              onDismiss={() => {
                setDesktopFilter(false);
                setMobileFilter(false);
              }}
              setClickOutside={(value?: boolean) =>
                setClickOutside(value ?? !clickOutside)
              }
            />
          }
          mobileFilterActive={mobileFilter}
          setMobileFilterActive={setMobileFilter}
          search={state.search}
          searchPlaceholder="Search supplier, business, user..."
          setSearch={storeActions.setSearch}
          tags={
            <>
              {!!state.business && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => storeActions.setBusiness(null)}
                  icon="FACTORY"
                >
                  {state.business.name}
                </Tag>
              )}
              {!!state.user && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => storeActions.setUser(null)}
                  icon="PROFILE"
                >
                  {state.user.name}
                </Tag>
              )}
              {state.orderStatuses.length > 0 && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => storeActions.setOrderStatuses([])}
                  icon="PROGRESS_HELPER"
                >
                  {state.orderStatuses
                    .map((status) => capitalize(status))
                    .join(", ")}
                </Tag>
              )}
              {(!!state.orderDateMin || !!state.orderDateMax) && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => {
                    storeActions.setOrderDateMin("");
                    storeActions.setOrderDateMax("");
                  }}
                  icon="CALENDAR_BLANK_OUTLINE"
                >
                  {`Order date ${state.orderDateMin ? dayjs(state.orderDateMin).format("DD/MM/YYYY") : "up to"} ${!!state.orderDateMax && !!state.orderDateMin && state.orderDateMin !== "" ? "-" : !!state.orderDateMin && !state.orderDateMax ? "+" : ""} ${!!state.orderDateMax ? dayjs(state.orderDateMax).format("DD/MM/YYYY") : ""}`}
                </Tag>
              )}
              {(!!state.orderTotalMin || !!state.orderTotalMax) && (
                <Tag
                  className="mt-2 mr-2"
                  onClick={() => {
                    storeActions.setOrderTotalMin("");
                    storeActions.setOrderTotalMax("");
                  }}
                  icon="CART"
                >
                  {`Order total ${state.orderTotalMin ? formatCurrency(parseInt(state.orderTotalMin) * 100) : "up to"} ${!!state.orderTotalMax && !!state.orderTotalMin && state.orderTotalMin !== "" ? "-" : !!state.orderTotalMin && !state.orderTotalMax ? "+" : ""} ${!!state.orderTotalMax ? formatCurrency(parseInt(state.orderTotalMax) * 100) : ""}`}
                </Tag>
              )}
            </>
          }
        />
        {isDesktop ? (
          <SellarCreditOrderTable
            setSelectedOrder={setSelectedOrder}
            setActive={setActive}
            handleTableSort={handleTableSort}
            pagination={state.pagination}
            setPagination={storeActions.setPagination}
            query={orderQuery}
            totalResults={totalResults}
          />
        ) : (
          <SellarCreditOrderCardList
            queryParams={queryParams}
            setSelectedOrder={setSelectedOrder}
            setActive={setActive}
          />
        )}
      </div>
      <SellarCreditOrderDrawer
        active={active}
        setActive={setActive}
        order={selectedOrder}
      />
    </PageCollectionLayout>
  );
};
